import React, { Component } from "react";
import {
  Alert,
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid as Container,
  Modal,
  Row,
  Table
} from "react-bootstrap";
import TimeAgo from "timeago-react";

import Card from "components/Card/Card.jsx";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://hgsystem.pl/mangusta";

const INPUT_DEFAULTS = [
  {
    input_id: 1,
    enabled: 1,
    label: "Mleko",
    unit: "l",
    function_type: 3
  },
  {
    input_id: 2,
    enabled: 1,
    label: "Temperatura",
    unit: "°C",
    function_type: 1
  },
  {
    input_id: 3,
    enabled: 0,
    label: "Wejście analogowe 3",
    unit: "",
    function_type: 0
  },
  {
    input_id: 4,
    enabled: 0,
    label: "Wejście analogowe 4",
    unit: "",
    function_type: 0
  }
];

const ADVANCED_FIELDS = [
  {
    key: "division_factor",
    label: "Dzielnik wartości",
    min: 1,
    max: 1000
  },
  { key: "cal_in_a", label: "Punkt A — ADC", min: -32768, max: 32767 },
  { key: "cal_out_a", label: "Punkt A — wynik", min: -32768, max: 32767 },
  { key: "cal_in_b", label: "Punkt B — ADC", min: -32768, max: 32767 },
  { key: "cal_out_b", label: "Punkt B — wynik", min: -32768, max: 32767 },
  {
    key: "adc_correction",
    label: "Korekta ADC",
    min: -10000,
    max: 10000
  },
  {
    key: "normal_correction_absolute",
    label: "Korekta bezwzględna",
    step: "0.001"
  },
  {
    key: "normal_correction_percent",
    label: "Korekta procentowa",
    step: "0.001"
  }
];

const USER_MEASUREMENT_FIELDS = [
  {
    key: "reading_interval_ms",
    label: "Częstotliwość odczytów [ms]",
    min: 1000,
    max: 600000,
    step: "100",
    help: "Od 1 sekundy do 10 minut."
  },
  {
    key: "averaging_samples",
    label: "Wielkość średniej [próbki]",
    min: 0,
    max: 1000,
    step: "1",
    help: "0 wyłącza uśrednianie; maksymalnie 1000 próbek."
  },
  {
    key: "min_change",
    label: "Minimalna zmiana",
    min: 0,
    max: 10000,
    step: "0.001",
    help: "0 rejestruje każdą zmianę; maksymalnie 10000 jednostek."
  }
];

const ADMIN_MEASUREMENT_FIELDS = [
  {
    key: "reading_interval_ms",
    label: "Częstotliwość odczytów [ms]",
    min: 10,
    max: 6000000,
    step: "10",
    help: "Zakres administratora: 10 ms–6 000 000 ms."
  },
  {
    key: "averaging_samples",
    label: "Wielkość średniej [próbki]",
    min: 0,
    max: 10000,
    step: "1",
    help: "Zakres administratora: 0–10 000 próbek."
  },
  {
    key: "min_change",
    label: "Minimalna zmiana",
    min: 0,
    max: 1000000,
    step: "0.001",
    help: "Zakres administratora: 0–1 000 000 jednostek."
  }
];

function defaultSettings() {
  return {
    revision: 0,
    updated_at: null,
    analog_inputs: INPUT_DEFAULTS.map(input => ({
      ...input,
      adc_input: null,
      adc_gain: null,
      division_factor: null,
      cal_in_a: null,
      cal_in_b: null,
      cal_out_a: null,
      cal_out_b: null,
      cal_for_adc: null,
      adc_correction: null,
      cal_for_normal: null,
      normal_correction_absolute: null,
      normal_correction_percent: null,
      reading_interval_ms: null,
      averaging_samples: null,
      min_change: null
    }))
  };
}

function normalizeSettings(settings) {
  const defaults = defaultSettings();
  if (!settings || !Array.isArray(settings.analog_inputs)) return defaults;

  const byId = {};
  settings.analog_inputs.forEach(input => {
    byId[Number(input.input_id)] = input;
  });
  return {
    revision: Number(settings.revision) || 0,
    updated_at: settings.updated_at || null,
    analog_inputs: defaults.analog_inputs.map(input => ({
      ...input,
      ...(byId[input.input_id] || {})
    }))
  };
}

class TableList extends Component {
  state = {
    showSettings: false,
    settingsDevice: null,
    analogInputs: [],
    saving: false,
    settingsError: "",
    settingsMessage: "",
    deviceSortKey: "desc",
    deviceSortDirection: "asc"
  };

  getTimeAgo = timestamp => {
    const numericTimestamp = Number(timestamp);
    if (!isFinite(numericTimestamp) || numericTimestamp <= 0)
      return "Brak danych";
    return <TimeAgo datetime={numericTimestamp * 1000} locale="pl_PL" />;
  };

  getValue = measurement => {
    if (!measurement || measurement.value === undefined) return "—";
    const value = Number(measurement.value);
    return isFinite(value) ? value.toFixed(2) : "—";
  };

  getDeviceSettings = deviceId =>
    normalizeSettings(
      (this.props.deviceSettings || {})[String(deviceId)]
    );

  openSettings = device => {
    const settings = this.getDeviceSettings(device.id);
    this.setState({
      showSettings: true,
      settingsDevice: device,
      analogInputs: settings.analog_inputs.map(input => ({ ...input })),
      saving: false,
      settingsError: "",
      settingsMessage: ""
    });
  };

  updateInput = (index, field, value) => {
    this.setState(previousState => {
      const analogInputs = previousState.analogInputs.map((input, inputIndex) =>
        inputIndex === index ? { ...input, [field]: value } : input
      );
      return { analogInputs, settingsError: "", settingsMessage: "" };
    });
  };

  saveSettings = () => {
    const { credentials } = this.props;
    const { settingsDevice, analogInputs } = this.state;
    if (!credentials || !settingsDevice) {
      this.setState({ settingsError: "Brak danych uwierzytelniających." });
      return;
    }
    if (analogInputs.some(input => !String(input.label || "").trim())) {
      this.setState({ settingsError: "Każde wejście musi mieć opis." });
      return;
    }

    this.setState({ saving: true, settingsError: "", settingsMessage: "" });
    fetch(`${API_BASE}/device_settings.php`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        auth: credentials,
        action: "update",
        device_id: String(settingsDevice.id),
        analog_inputs: analogInputs
      })
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok)
          throw new Error(data.error || "Nie udało się zapisać ustawień.");
        return data;
      })
      .then(data => {
        if (this.props.onDeviceSettingsChange)
          this.props.onDeviceSettingsChange(settingsDevice.id, data.settings);
        this.setState({
          analogInputs: normalizeSettings(data.settings).analog_inputs,
          saving: false,
          settingsMessage: "Ustawienia zostały zapisane."
        });
      })
      .catch(error =>
        this.setState({ saving: false, settingsError: error.message })
      );
  };

  renderNullableBoolean(index, field, label) {
    const value = this.state.analogInputs[index][field];
    return (
      <FormGroup>
        <ControlLabel>{label}</ControlLabel>
        <FormControl
          componentClass="select"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={event =>
            this.updateInput(index, field, event.target.value)
          }
        >
          <option value="">Nie zmieniaj w sterowniku</option>
          <option value="1">Włączone</option>
          <option value="0">Wyłączone</option>
        </FormControl>
      </FormGroup>
    );
  }

  renderFunctionSetting(input, index) {
    return (
      <FormGroup style={{ marginTop: 12 }}>
        <ControlLabel>Funkcja pomiarowa</ControlLabel>
        <FormControl
          componentClass="select"
          value={
            input.function_type === null ||
            input.function_type === undefined
              ? ""
              : input.function_type
          }
          onChange={event =>
            this.updateInput(index, "function_type", event.target.value)
          }
        >
          <option value="">Nie zmieniaj</option>
          <option value="0">Brak funkcji specjalnej</option>
          <option value="1">Pomiar temperatury</option>
          <option value="2">
            Poziom medium — tabela litrażu (obsługa odbiorów)
          </option>
          <option value="3">
            Poziom medium — przepływomierz (obsługa odbiorów)
          </option>
        </FormControl>
        <span className="help-block">
          Odbiory można rejestrować dla wejść mierzących poziom medium.
        </span>
      </FormGroup>
    );
  }

  renderAdvancedSettings(input, index) {
    if (!this.props.isAdmin) return null;
    return (
      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>
          Ustawienia zaawansowane administratora
        </summary>
        <div style={{ paddingTop: 12 }}>
          <Row>
            <Col sm={4}>
              <FormGroup>
                <ControlLabel>Jednostka</ControlLabel>
                <FormControl
                  value={input.unit || ""}
                  maxLength={16}
                  onChange={event =>
                    this.updateInput(index, "unit", event.target.value)
                  }
                />
              </FormGroup>
            </Col>
            <Col sm={4}>
              <FormGroup>
                <ControlLabel>Wejście ADC</ControlLabel>
                <FormControl
                  componentClass="select"
                  value={input.adc_input === null ? "" : input.adc_input}
                  onChange={event =>
                    this.updateInput(index, "adc_input", event.target.value)
                  }
                >
                  <option value="">Nie zmieniaj</option>
                  <option value="0">AIN0 - AIN1</option>
                  <option value="1">AIN2 - AIN3</option>
                  <option value="2">AIN0 - GND</option>
                  <option value="3">AIN1 - GND</option>
                  <option value="4">AIN2 - GND</option>
                  <option value="5">AIN3 - GND</option>
                </FormControl>
              </FormGroup>
            </Col>
            <Col sm={4}>
              <FormGroup>
                <ControlLabel>Wzmocnienie ADC</ControlLabel>
                <FormControl
                  componentClass="select"
                  value={input.adc_gain === null ? "" : input.adc_gain}
                  onChange={event =>
                    this.updateInput(index, "adc_gain", event.target.value)
                  }
                >
                  <option value="">Nie zmieniaj</option>
                  <option value="0">2/3× (6,144 V)</option>
                  <option value="1">1× (4,096 V)</option>
                  <option value="2">2× (2,048 V)</option>
                  <option value="3">4× (1,024 V)</option>
                  <option value="4">8× (0,512 V)</option>
                  <option value="5">16× (0,256 V)</option>
                </FormControl>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              {this.renderNullableBoolean(
                index,
                "cal_for_adc",
                "Korekta ADC"
              )}
            </Col>
            <Col sm={6}>
              {this.renderNullableBoolean(
                index,
                "cal_for_normal",
                "Korekta wartości"
              )}
            </Col>
          </Row>
          <Row>
            {ADVANCED_FIELDS.map(field => (
              <Col sm={4} key={field.key}>
                <FormGroup>
                  <ControlLabel>{field.label}</ControlLabel>
                  <FormControl
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step || "1"}
                    value={
                      input[field.key] === null ||
                      input[field.key] === undefined
                        ? ""
                        : input[field.key]
                    }
                    onChange={event =>
                      this.updateInput(index, field.key, event.target.value)
                    }
                  />
                </FormGroup>
              </Col>
            ))}
          </Row>
        </div>
      </details>
    );
  }

  renderMeasurementSettings(input, index) {
    const fields = this.props.isAdmin
      ? ADMIN_MEASUREMENT_FIELDS
      : USER_MEASUREMENT_FIELDS;
    return (
      <div style={{ marginTop: 12 }}>
        <ControlLabel>Podstawowe ustawienia rejestracji</ControlLabel>
        <Row>
          {fields.map(field => (
            <Col sm={4} key={field.key}>
              <FormGroup>
                <ControlLabel>{field.label}</ControlLabel>
                <FormControl
                  type="number"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={
                    input[field.key] === null ||
                    input[field.key] === undefined
                      ? ""
                      : input[field.key]
                  }
                  placeholder="Bez zmiany"
                  onChange={event =>
                    this.updateInput(index, field.key, event.target.value)
                  }
                />
                <span className="help-block">{field.help}</span>
              </FormGroup>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  renderSettingsModal() {
    const device = this.state.settingsDevice;
    return (
      <Modal
        show={this.state.showSettings}
        onHide={() => this.setState({ showSettings: false })}
        bsSize="large"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Wejścia analogowe — {device ? device.desc || device.id : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Włączone wejścia są dostępne w ustawieniach wykresów. Opis może być
            inny dla każdego urządzenia.
          </p>
          {this.state.settingsError && (
            <Alert bsStyle="danger">{this.state.settingsError}</Alert>
          )}
          {this.state.settingsMessage && (
            <Alert bsStyle="success">{this.state.settingsMessage}</Alert>
          )}
          {this.state.analogInputs.map((input, index) => (
            <div
              key={input.input_id}
              style={{
                border: "1px solid #ddd",
                borderRadius: 4,
                marginBottom: 12,
                padding: 14
              }}
            >
              <Row>
                <Col sm={3}>
                  <ControlLabel>Wejście {input.input_id}</ControlLabel>
                  <div className="checkbox">
                    <input
                      id={`analog-enabled-${input.input_id}`}
                      type="checkbox"
                      checked={Number(input.enabled) === 1}
                      onChange={event =>
                        this.updateInput(
                          index,
                          "enabled",
                          event.target.checked ? 1 : 0
                        )
                      }
                    />
                    <label htmlFor={`analog-enabled-${input.input_id}`}>
                      Dostępne na wykresach
                    </label>
                  </div>
                </Col>
                <Col sm={9}>
                  <FormGroup>
                    <ControlLabel>Opis</ControlLabel>
                    <FormControl
                      value={input.label || ""}
                      maxLength={64}
                      onChange={event =>
                        this.updateInput(index, "label", event.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              {this.renderFunctionSetting(input, index)}
              {this.renderMeasurementSettings(input, index)}
              {this.renderAdvancedSettings(input, index)}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => this.setState({ showSettings: false })}>
            Zamknij
          </Button>
          <Button
            bsStyle="primary"
            disabled={this.state.saving}
            onClick={this.saveSettings}
          >
            {this.state.saving ? "Zapisywanie…" : "Zapisz ustawienia"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderMeasurements(device) {
    const measurements =
      (this.props.lastMeasurement || {})[device.id] || [];
    const settings = this.getDeviceSettings(device.id);
    const enabledInputs = settings.analog_inputs.filter(
      input => Number(input.enabled) === 1
    );
    if (enabledInputs.length === 0) return "Brak włączonych wejść";

    return enabledInputs.map(input => {
      const measurement = measurements.find(
        item => Number(item.input_id) === Number(input.input_id)
      );
      return (
        <div key={input.input_id} style={{ marginBottom: 4 }}>
          <strong>{input.label}:</strong> {this.getValue(measurement)}
          {input.unit ? ` ${input.unit}` : ""}{" "}
          <span className="text-muted">
            ({this.getTimeAgo(measurement && measurement.timestamp)})
          </span>
        </div>
      );
    });
  }

  changeDeviceSort = key => {
    this.setState(previousState => ({
      deviceSortKey: key,
      deviceSortDirection:
        previousState.deviceSortKey === key &&
        previousState.deviceSortDirection === "asc"
          ? "desc"
          : "asc"
    }));
  };

  sortedDevices = devices => {
    const { deviceSortKey, deviceSortDirection } = this.state;
    const direction = deviceSortDirection === "desc" ? -1 : 1;
    return devices.slice().sort((left, right) => {
      const leftValue = String(left[deviceSortKey] || "");
      const rightValue = String(right[deviceSortKey] || "");
      return (
        leftValue.localeCompare(rightValue, "pl", {
          sensitivity: "base",
          numeric: true
        }) * direction
      );
    });
  };

  sortableDeviceHeader = (label, key) => {
    const active = this.state.deviceSortKey === key;
    const arrow = active
      ? this.state.deviceSortDirection === "asc"
        ? " ▲"
        : " ▼"
      : "";
    return (
      <th>
        <button
          type="button"
          onClick={() => this.changeDeviceSort(key)}
          style={{
            border: 0,
            padding: 0,
            background: "transparent",
            font: "inherit",
            fontWeight: "bold",
            cursor: "pointer",
            whiteSpace: "nowrap"
          }}
        >
          {label}
          {arrow}
        </button>
      </th>
    );
  };

  render() {
    const devices = Array.isArray(this.props.device) ? this.props.device : [];
    const sortedDevices = this.sortedDevices(devices);
    const lastMessages = this.props.lastMessage || {};

    return (
      <div className="content">
        <Container fluid>
          {this.renderSettingsModal()}
          <Row>
            <Col md={12}>
              <Card
                title="Lista zainstalowanych urządzeń"
                category="Urządzenia przypisane do konta"
                ctTableFullWidth
                ctTableResponsive
                content={
                  <Table striped hover>
                    <thead>
                      <tr>
                        {this.sortableDeviceHeader("Urządzenie", "desc")}
                        {this.sortableDeviceHeader("ID", "id")}
                        {this.sortableDeviceHeader("SW-wersja", "sw_version")}
                        {this.sortableDeviceHeader("HW-wersja", "hw_version")}
                        {this.sortableDeviceHeader("Status", "status")}
                        <th>Ustawienia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDevices.map(device => (
                        <tr key={device.id}>
                          <td>{device.desc}</td>
                          <td>{device.id}</td>
                          <td>{device.sw_version}</td>
                          <td>{device.hw_version}</td>
                          <td>{device.status}</td>
                          <td>
                            <Button
                              bsStyle="info"
                              bsSize="small"
                              onClick={() => this.openSettings(device)}
                            >
                              Wejścia analogowe
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                }
              />
            </Col>
            <Col md={12}>
              <Card
                title="Ostatnio zarejestrowane pomiary"
                category="Pomiary z wejść dostępnych na wykresach"
                ctTableFullWidth
                ctTableResponsive
                content={
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Urządzenie</th>
                        <th>Ostatnie połączenie</th>
                        <th>Ostatnie pomiary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDevices.map(device => {
                        const messages = lastMessages[device.id] || [];
                        return (
                          <tr key={device.id}>
                            <td>{device.desc || device.id}</td>
                            <td>
                              {this.getTimeAgo(
                                messages[0] && messages[0].timestamp
                              )}
                            </td>
                            <td>{this.renderMeasurements(device)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                }
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default TableList;



// WEBPACK FOOTER //
// ./src/views/TableList/TableList.jsx